import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Angry,
  BookOpen,
  Frown,
  Laugh,
  Meh,
  MessageCircle,
  MessageCircleQuestion,
  Smile,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { useLanguageRouter } from "@/hooks/useLanguageRouter";

const feedbackOptions = [
  { value: 1, label: "Very unhappy", icon: Angry },
  { value: 2, label: "Unhappy", icon: Frown },
  { value: 3, label: "Neutral", icon: Meh },
  { value: 4, label: "Happy", icon: Smile },
  { value: 5, label: "Delighted", icon: Laugh },
] as const;

type FeedbackDialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export default function FeedbackDialog({
  open: controlledOpen,
  onOpenChange,
  hideTrigger,
}: FeedbackDialogProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const submitFeedback = trpc.system.submitFeedback.useMutation({
    onSuccess: () => {
      toast.success("Thanks for the feedback!");
      setOpen(false);
      setRating(null);
      setMessage("");
    },
  });

  useEffect(() => {
    if (submitFeedback.error) {
      toast.error(submitFeedback.error.message || "Failed to send feedback");
    }
  }, [submitFeedback.error]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setRating(null);
      setMessage("");
    }
  };

  const handleSubmit = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      toast.error("Please enter your feedback before submitting.");
      return;
    }

    const page = `${window.location.pathname}${window.location.search}`;
    submitFeedback.mutate({
      message: trimmedMessage,
      rating: rating ?? undefined,
      page,
    });
  };

  const isSubmitting = submitFeedback.isPending;
  const canSubmit = message.trim().length > 0 && !isSubmitting;
  const { withLanguagePath } = useLanguageRouter();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!hideTrigger && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5"
              aria-label="Help and feedback"
              title="Help"
            >
              <MessageCircleQuestion className="h-[18px] w-[18px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onSelect={() => setOpen(true)}>
              <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
              Feedback
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                window.location.href = withLanguagePath("/help");
              }}
            >
              <BookOpen className="mr-2 h-4 w-4" aria-hidden="true" />
              Help Center
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <DialogContent className="sm:max-w-lg bg-gray-950 border-gray-800 p-6 sm:p-7 text-white">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl sm:text-2xl font-semibold text-white">
            Your feedback helps us improve
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2">
          {feedbackOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = rating === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setRating(option.value)}
                aria-label={option.label}
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full border transition-all",
                  isSelected
                    ? "border-primary/80 bg-primary/15 text-primary shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
                    : "border-white/10 text-muted-foreground hover:border-white/30 hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
              </button>
            );
          })}
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          This form is for product feedback and feature requests only. For help
          articles and guides, visit our{" "}
          <a
            href="https://solmi.ai/help"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white underline underline-offset-4 hover:text-primary"
          >
            Help Center
          </a>
          . For technical issues or account support, please email{" "}
          <a
            href="mailto:support@solmi.ai"
            className="text-white underline underline-offset-4 hover:text-primary"
          >
            support@solmi.ai
          </a>
          .
        </p>

        <Textarea
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Tell us more about your experience."
          rows={5}
          maxLength={2000}
          className="min-h-[140px] bg-black/30 border-white/10 text-white placeholder:text-muted-foreground focus-visible:border-primary/60"
        />

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="min-w-[120px] bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-60"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
